import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { VERSION_CONSTANTS, whitelistReturnUrl } from './version.constants';

/**
 * Captures the in-app path before a forced loginRedirect so post-login lands
 * back where the user was, and consumes it once authentication completes.
 *
 * Whitelisted upstream against open-redirect (cf. version.constants).
 */
@Injectable({ providedIn: 'root' })
export class ReturnUrlService {
  private readonly router = inject(Router);
  private readonly storageKey = VERSION_CONSTANTS.RETURN_URL_STORAGE_KEY;

  capture(currentUrl: string): void {
    const safe = whitelistReturnUrl(currentUrl);
    if (!safe) return;
    try {
      sessionStorage.setItem(this.storageKey, safe);
    } catch {
      // sessionStorage can throw in Safari private mode; the user will simply
      // land on the default route after login. Failing the redirect on this
      // would be worse.
    }
  }

  /**
   * Captures where the user actually is, or is headed. Router.url stays '/'
   * until the first navigation commits, so a forced logout during the boot of
   * a deep-linked tab (retour UAT 008 sibling) would otherwise record the home
   * page and lose the destination. A navigation in flight wins over router.url.
   */
  captureCurrent(): void {
    const navigation = this.router.getCurrentNavigation();
    const target = navigation?.finalUrl ?? navigation?.initialUrl;
    this.capture(target ? this.router.serializeUrl(target) : this.router.url);
  }

  consume(): string | null {
    try {
      const raw = sessionStorage.getItem(this.storageKey);
      sessionStorage.removeItem(this.storageKey);
      return whitelistReturnUrl(raw);
    } catch {
      return null;
    }
  }

  /** Reads the stored URL (if any) and navigates to it; no-op otherwise. */
  consumeAndNavigate(): void {
    const target = this.consume();
    if (target && target !== this.router.url) {
      void this.router.navigateByUrl(target);
    }
  }
}
